/**
 * Client-local weapon-skin preference (docs/systems/weapon-skins.md) — which
 * cosmetic the LOCAL player wants shown on each equipment slot, self-view only.
 *
 * There is deliberately no wardrobe/loadout on the wire (P4): the preference is
 * a per-browser `localStorage` value (`gc.skin.<slot>` = cosmetic id) plus a
 * dev bridge (`window.__GC_DEVSKIN`, gated by the same `devToolsEnabled()` as
 * `__GC_DEVJUMP`) so the visual lane and a playtester can flip skins without a
 * UI. Other players never see it; ownership is not checked (both are the wire
 * half, a backlog story) — so the renderer reads it in dev builds only
 * (`skinPreferenceForBuild`). There it consults `preferredSkin(slot)` on every
 * sync and re-attaches when `subscribeSkinPreference` fires.
 *
 * Pure core + thin browser edge, the devFlags.ts split: `createSkinPreferenceStore`
 * takes an injectable `SkinStorage` so the whole thing runs under `node --test`
 * (AD-120); every storage access is try/caught (a private window, a blocked
 * store, or no DOM at all is a "no preference", never a throw). Presentation
 * only, off the per-tick path, no shared/server imports (AD-122).
 */

/** localStorage key prefix; the suffix is the equipment slot (`gc.skin.mainhand`). */
export const SKIN_STORAGE_PREFIX = 'gc.skin.';

/** The slice of the Web Storage API the store needs (a real `Storage` satisfies it). */
export interface SkinStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  /** Enumerate keys — `Storage.length` + `key(i)`; a fake may return the array directly. */
  readonly length: number;
  key(index: number): string | null;
}

export interface SkinPreferenceStore {
  /** The preferred cosmetic id for a slot, or `null` (base mesh). */
  get(slot: string): string | null;
  /** Set (non-empty id) or clear (`null`/`''`) the preference; notifies listeners on change. */
  /**
   * Store (or clear, with `null`) the preference. Returns `true` when the store
   * now holds the requested value (written, or already equal); `false` when it
   * could not — empty slot, no storage, or a write that threw. A caller that
   * drives the page blind (the visual lane) needs that verdict; `void` was a rig
   * that reported PASS on a preference it never stored.
   */
  set(slot: string, cosmeticId: string | null): boolean;
  /** Every stored preference, slot → cosmetic id. */
  list(): Record<string, string>;
  /** Subscribe to changes; returns the unsubscribe function. */
  subscribe(listener: () => void): () => void;
}

function safeStorage(provider: () => SkinStorage | null): SkinStorage | null {
  try {
    return provider();
  } catch {
    return null;
  }
}

/**
 * Build a store over an injectable storage provider. The provider is invoked per
 * access (never cached) so a storage that becomes unavailable mid-session
 * degrades to "no preference" rather than throwing stale references.
 */
export function createSkinPreferenceStore(
  provider: () => SkinStorage | null,
  prefix: string = SKIN_STORAGE_PREFIX,
): SkinPreferenceStore {
  const listeners = new Set<() => void>();
  const notify = (): void => {
    for (const listener of [...listeners]) listener();
  };
  const get = (slot: string): string | null => {
    const store = safeStorage(provider);
    if (store === null || slot === '') return null;
    try {
      const raw = store.getItem(`${prefix}${slot}`);
      return raw === null || raw === '' ? null : raw;
    } catch {
      return null;
    }
  };
  return {
    get,
    set(slot, cosmeticId) {
      if (slot === '') return false;
      const store = safeStorage(provider);
      if (store === null) return false;
      const next = cosmeticId === null || cosmeticId === '' ? null : cosmeticId;
      if (get(slot) === next) return true; // unchanged ⇒ no re-attach churn, but held
      try {
        if (next === null) store.removeItem(`${prefix}${slot}`);
        else store.setItem(`${prefix}${slot}`, next);
      } catch {
        return false;
      }
      notify();
      return true;
    },
    list() {
      const out: Record<string, string> = {};
      const store = safeStorage(provider);
      if (store === null) return out;
      try {
        for (let i = 0; i < store.length; i += 1) {
          const key = store.key(i);
          if (key === null || !key.startsWith(prefix)) continue;
          const value = store.getItem(key);
          if (value !== null && value !== '') out[key.slice(prefix.length)] = value;
        }
      } catch {
        return out;
      }
      return out;
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

// ---------------------------------------------------------------------------
// Browser side — the module singleton over `localStorage` + the dev bridge.
// ---------------------------------------------------------------------------

function browserStorage(): SkinStorage | null {
  return typeof localStorage === 'undefined' ? null : localStorage;
}

const store = createSkinPreferenceStore(browserStorage);

/** The preferred cosmetic id for a slot, or `null` — the renderer's `SkinPreference`. */
export function preferredSkin(slot: string): string | null {
  return store.get(slot);
}

/** Set or clear (`null`) the preference for a slot; listeners fire on change. Returns the store's verdict. */
export function setPreferredSkin(slot: string, cosmeticId: string | null): boolean {
  return store.set(slot, cosmeticId);
}

/**
 * The skin source the renderer gets in THIS build: the stored preference in dev
 * builds, `undefined` (base mesh only) in production. Ownership is not checked
 * anywhere yet, so a production read would let a hand-edited `gc.skin.*` key wear
 * a premium (`catalogue`) skin for free — a store-trust leak even self-view only
 * (`q-weapon-skin-premium-self-apply`). Production loses nothing: the dev bridge
 * is the only writer. Lift this gate when server ownership lands.
 */
export function skinPreferenceForBuild(
  devTools: boolean,
  read: (slot: string) => string | null = preferredSkin,
): ((slot: string) => string | null) | undefined {
  return devTools ? read : undefined;
}

/** Every stored preference, slot → cosmetic id. */
export function listSkinPreferences(): Record<string, string> {
  return store.list();
}

/** Subscribe to preference changes (the renderer's re-attach hook). */
export function subscribeSkinPreference(listener: () => void): () => void {
  return store.subscribe(listener);
}

/**
 * The shape of `window.__GC_DEVSKIN` (dev builds only). `set` answers with a
 * VERDICT, not `void`: `true` only when the preference is stored AND — when the
 * installer supplied a probe — the game confirms the skin actually applies to
 * what is equipped in that slot (kind `weapon-skin`, matching archetype, pack
 * delivered). A blind driver (the visual lane) reads that verdict; a `true`
 * that later shows the base weapon would be a rig reporting on a scene it never
 * reached (`q-visual-lane-ignores-unknown-flags`).
 */
export interface SkinDevBridge {
  set(slot: string, cosmeticId: string): boolean;
  clear(slot: string): boolean;
  list(): Record<string, string>;
}

/**
 * Does `cosmeticId` resolve as a skin for what is currently equipped in `slot`?
 * Supplied by Game from the live equipment renderer; absent in tests/off-DOM.
 */
export type SkinApplicabilityProbe = (slot: string, cosmeticId: string) => boolean;

declare global {
  interface Window {
    /**
     * Playtest weapon-skin switch (weapon-skins.md): `set(slot, cosmeticId)` /
     * `clear(slot)` / `list()`. Installed by Game in dev builds only, under the
     * same `devToolsEnabled()` gate as `__GC_DEVJUMP`; also the visual lane's
     * only handle on the skin surface (it drives the page over CDP and sees
     * `window` alone). Self-view, localStorage-backed, nothing on the wire.
     */
    __GC_DEVSKIN?: SkinDevBridge;
  }
}

/**
 * Install `window.__GC_DEVSKIN` (or remove it with `null`). The caller gates on
 * `devToolsEnabled()`; this function only does the assignment, safely off-DOM.
 * Returns the uninstaller.
 */
export function installSkinDevBridge(
  target: Window | null = globalWindow(),
  probe?: SkinApplicabilityProbe,
): () => void {
  if (target === null) return () => undefined;
  target.__GC_DEVSKIN = {
    set: (slot, cosmeticId) => {
      // Probe FIRST, then store: a skin that cannot apply is refused without
      // leaving a dangling preference behind for the next session to trip on.
      if (probe !== undefined && !probe(slot, cosmeticId)) return false;
      return setPreferredSkin(slot, cosmeticId);
    },
    clear: (slot) => setPreferredSkin(slot, null),
    list: () => listSkinPreferences(),
  };
  return () => {
    if (target.__GC_DEVSKIN !== undefined) delete target.__GC_DEVSKIN;
  };
}

function globalWindow(): Window | null {
  try {
    return typeof window === 'undefined' ? null : window;
  } catch {
    return null;
  }
}
